import { useCallback, useEffect, useState } from "react";
import { usePrevious } from "react-use";
import { AppointmentStatus } from "@scrile/api-provider/dist/api/AppointmentProvider";
import { InputReviewSearch } from "@scrile/api-provider/dist/api/ReviewProvider";
import { throttle } from "lodash-es";
import config from "../../config";
import useAuthUser from "../../hooks/useAuthUser";
import emitter, { EVENTS } from "../../lib/emitter";
import providers from "../../lib/providers";

export default function useController() {
  const { user } = useAuthUser();
  const prevUser = usePrevious(user);

  const [unreadThreadsCount, setUnreadThreadsCount] = useState(0);

  const [countUpcomingEvents, setCountUpcomingEvents] = useState(0);
  const isPerformer: boolean = user?.role === config.userPerformerRole;

  const getAppointmentsBadgeCount = useCallback(async () => {
    if (!user) {
      setCountUpcomingEvents(0);
      return;
    }

    try {
      const upcomingResponse = await providers.AppointmentProvider.search({
        ...(isPerformer ? { providerId: user.id } : { consumerId: user.id }),
        startTimeFrom: new Date().toISOString(),
        status: [AppointmentStatus.NEW, AppointmentStatus.APPROVED],
        size: 0,
      });

      let unreadFeedbackCount = 0;
      if (!isPerformer) {
        try {
          const unreadFeedbackResponse = await providers.ReviewProvider.find({
            data: {
              subjectType: "appointment",
              read: false,
              size: 0,
            } as InputReviewSearch,
          });
          unreadFeedbackCount = unreadFeedbackResponse?.totalRecords ?? 0;
        } catch {
          unreadFeedbackCount = 0;
        }
      }

      setCountUpcomingEvents((upcomingResponse?.totalRecords ?? 0) + unreadFeedbackCount);
    } catch {
      // keep previous badge value on failure
    }
  }, [isPerformer, user]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledCountOfThreadListener = useCallback(
    throttle(() => {
      providers.MessageThreadsProvider.getUnreadCount().then((r) => setUnreadThreadsCount(r));
    }, 1000),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledAppointmentsBadgeListener = useCallback(
    throttle(() => {
      getAppointmentsBadgeCount();
    }, 1000),
    [getAppointmentsBadgeCount]
  );

  useEffect(() => {
    if (!prevUser && user) {
      throttledCountOfThreadListener();
      getAppointmentsBadgeCount();
      emitter.on(EVENTS.MESSAGES_NEW_MESSAGE, throttledCountOfThreadListener);
      emitter.on(EVENTS.MESSAGES_MARKED_READ, throttledCountOfThreadListener);
      emitter.on(EVENTS.APPOINTMENT_FEEDBACK_MARKED_READ, throttledAppointmentsBadgeListener);
    } else if (prevUser && !user) {
      emitter.off(EVENTS.MESSAGES_NEW_MESSAGE, throttledCountOfThreadListener);
      emitter.off(EVENTS.MESSAGES_MARKED_READ, throttledCountOfThreadListener);
      emitter.off(EVENTS.APPOINTMENT_FEEDBACK_MARKED_READ, throttledAppointmentsBadgeListener);
    }

    return () => {
      emitter.off(EVENTS.MESSAGES_NEW_MESSAGE, throttledCountOfThreadListener);
      emitter.off(EVENTS.MESSAGES_MARKED_READ, throttledCountOfThreadListener);
      emitter.off(EVENTS.APPOINTMENT_FEEDBACK_MARKED_READ, throttledAppointmentsBadgeListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, throttledCountOfThreadListener, throttledAppointmentsBadgeListener, getAppointmentsBadgeCount]);

  const onClickMemberBalance = () => {
    emitter.emit(EVENTS.BALANCE_REFILL_SHOW);
  };

  return {
    onClickMemberBalance,
    isPerformer,
    countUpcomingEvents,
    user,
    unreadThreadsCount,
  };
}
